// src/component/common/FaviconImage.jsx - Fixed version
import React, { useState, useMemo, useCallback } from 'react';
import { Globe } from 'lucide-react';

const FaviconImage = ({ domain, size = 16, className = "", faviconUrl = null }) => {
  const [faviconError, setFaviconError] = useState(false);
  const [googleFaviconFailed, setGoogleFaviconFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Clean domain helper
  const cleanDomainName = useCallback((domainInput) => {
    if (!domainInput || typeof domainInput !== 'string') {
      return null;
    }

    let cleanDomain = domainInput;
    try {
      if (domainInput.includes('://')) {
        cleanDomain = new URL(domainInput).hostname;
      }
      cleanDomain = cleanDomain.replace(/^www\./, '');
    } catch (e) {
      cleanDomain = domainInput.replace(/^www\./, '');
    }

    return cleanDomain;
  }, []);

  // Check if backend favicon URL is valid
  const hasValidBackendFavicon = faviconUrl &&
    typeof faviconUrl === 'string' &&
    !faviconUrl.includes('question.webp') &&
    faviconUrl.trim() !== '';

  // Build backend favicon URL
  const getBackendFaviconUrl = useCallback(() => {
    if (!hasValidBackendFavicon) return null;
    return faviconUrl.startsWith('http') ? faviconUrl : `https://mars2.mediacle.net/${faviconUrl}`;
  }, [hasValidBackendFavicon, faviconUrl]);

  // Compute favicon URL - prioritize backend favicon if available, then Google
  const faviconSrc = useMemo(() => {
    if (faviconError) return null;

    // If we have a valid backend favicon, try it first
    if (hasValidBackendFavicon && !googleFaviconFailed) {
      return getBackendFaviconUrl();
    }

    // If backend favicon failed or not available, try Google
    if (!domain) return null;
    const cleanDomain = cleanDomainName(domain);
    if (cleanDomain) {
      return `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=${size}`;
    }
    return null;
  }, [domain, size, faviconError, googleFaviconFailed, hasValidBackendFavicon, getBackendFaviconUrl, cleanDomainName]);

  const handleFaviconError = () => {
    // If backend favicon fails, try Google
    if (hasValidBackendFavicon && !googleFaviconFailed) {
      setGoogleFaviconFailed(true); // This will trigger switch to Google favicon
    } else if (!faviconError) {
      setFaviconError(true);
    }
  };

  const handleImageLoad = (e) => {
    setImageLoaded(true);
    // Check if image is too small (likely a placeholder from Google)
    if (e.target.naturalWidth <= 1 || e.target.naturalHeight <= 1) {
      handleFaviconError();
    }
  };

  // Show fallback if no favicon source or no domain
  if (!faviconSrc || !domain) {
    return (
      <div
        className={`bg-gray-200 rounded-sm flex items-center justify-center ${className}`}
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <Globe className="w-3 h-3 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={faviconSrc}
      alt={`${domain} favicon`}
      className={`rounded-sm ${className}`}
      style={{ width: `${size}px`, height: `${size}px` }}
      onLoad={handleImageLoad}
      onError={handleFaviconError}
    />
  );
};

export default FaviconImage;